import constants from "../../../../appConstants";
import {
  formatDateTime,
  formatDate,
  suppressEnter,
  mergeUserConfig,
  formatDeliveryWindow,
  customAlphanumericComparator,
  suppressKeyboardEventHandler,
} from '@dataGrid/gridUtilities';
import { MultiSelectFilter, DateTimeFilter, StaticFilter } from '@dataGrid/components/columnFilters';
import {
  DropdownEditor,
  DateTimeEditor,
  NumericEditor,
  TextFieldEditor,
  TimeRangeEditor
} from '@dataGrid/components/cellEditors';
import iconRenderer from "../../../common/iconRenderer"
import { DropdownValueCellRenderer,DotRenderer, IBETARenderer, SpecialNeedsRenderer, SpecialNeedsValueGetter,GeoCodeRenderer } from '@dataGrid/components/cellRenderer/index';
import { CopyTextCellRenderer } from '@dataGrid/components/cellRenderer';
import { COLUMN_CONFIG } from "./constant";
import {
  getAPTWindowConfig,
  ibEtaValueFormatter,
  getCustomerName,
  getCustomerAddress,
  getCustomerCity
} from './utils';

export default class GridConfig {
    constructor(configuration){
        const config = configuration || {};
        this.name = "GridConfiguration";
        this.config = GridConfig.getConfiguration(config);
    }

      static getConfiguration({
        name,
        userConfig,
        featureFlags,
        terminalAccessLabel,
        ROUTENAME_EDITOR_PARAMS,
        columnConfig,
        enableFixedEquipmentSize,
        shouldShowTwoManInTrailerInventory,
        enableNoCargoVanCustomer,
        isShipementForecastView = false,
        enableRevenueFieldsOnUI
      }) {
      const defaultConfig = GridConfig.getDefaultConfiguration({
        name,
        featureFlags,
        terminalAccessLabel,
        ROUTENAME_EDITOR_PARAMS,
        columnConfig,
        enableFixedEquipmentSize,
        shouldShowTwoManInTrailerInventory,
        enableNoCargoVanCustomer,
        isShipementForecastView,
        enableRevenueFieldsOnUI
      });
      return mergeUserConfig({
        defaultConfig,
          userConfig,
      });
    }

    static isRouteNameEditable(params){
      return params.data.isInPlan;
    }

    static getProNumberClasses(params){
      return  ["gridCell-leftAlign", params.context?.hasProImagesURL ?  'actionable-cell'  :""];
    }

    static getSpecialNeedsOption(params) {
      return params.context?.specialNeedsOptions;
    }

    static specialNeedsValueGetter(params, featureFlags) {
      let overridenSpecialNeeds = {};
      if (!featureFlags?.enableUnattendedDeliveryInCustomerManagement) {
        overridenSpecialNeeds = {
          isUnattendedDelivery: false
        };
      }
      return SpecialNeedsValueGetter({
        data: {
          ...params.data,
          ...overridenSpecialNeeds
        }
      });
    }

    static IBETAEditable(params){
      if(params.data.actualArrivalTime){
        return false;
      }
      return true;
    }

    static shipmentTypeValueGetter(params){
      return params.data.isDelivery ? "D": "P";
    }

    static getCustomerState(params) {
      return params.data.isDelivery
        ? params.data.state
        : params.data.shipperState;
    }

    static getCustomerZip(params) {
      return params.data.isDelivery
        ? params.data.zip
        : params.data.shipperZip;
    }

    static getCustomerCode(params) {
      return params.data.isDelivery
        ? params.data.displayCustomerCode
        : params.data.shipperCode;
    }

    static getShipperFullAddress(params) {
      return [params.data.shipperAddress, params.data.shipperAddress2].filter(Boolean).join(', ');
    }

    static geoCodeValueGetter(params) {  
      return params.data.isDelivery ? params.data.isGeoCoded: params.data.isShipperGeoCoded;
    }

    static getCustomerNameClasses(params) {
      if (params?.context?.isShipementForecastView) {
        return ['gridCell-leftAlign'];
      }
      return [
        'gridCell-leftAlign',
        params.value ? 'actionable-cell' : ''
      ];
    }

      static getDefaultConfiguration({
        name,
        featureFlags,
        terminalAccessLabel,
        ROUTENAME_EDITOR_PARAMS,
        columnConfig,
        enableFixedEquipmentSize,
        shouldShowTwoManInTrailerInventory,
        enableNoCargoVanCustomer,
        isShipementForecastView,
        enableRevenueFieldsOnUI
      }) {
        const aptConfig = getAPTWindowConfig(columnConfig);

        const isEditAllowed = !isShipementForecastView;
        const defaultConfig = {
        isEditAllowed, 
        title: name || constants.gridNames.ShipmentsGrid,
        keyColumn: "proId",
        rowSelection: "multiple",
        rowDeselection: "true",
        defaultColDef:{
          cellEditor: TextFieldEditor,
          sortable: true,
          filter: true,
          resizable: true,
          cellDataType: false,
          suppressKeyboardEvent: suppressKeyboardEventHandler(),
        },
        columns: [
          {
            headerName: "",
            minWidth: constants.columnWidth.VIOLATION_DOT,
            maxWidth: constants.columnWidth.VIOLATION_DOT,
            field:"violation",
            pinned: "left",
            lockPosition: true,
            lockPinned: true,
            cellClass: ["gridCell-centerAlign", "gridCell-dot"],
            filter: false,
            cellRenderer: DotRenderer,
            isInvisibleInShowHide: true,
            gridTitle: constants.gridNames.IBShipmentsGrid,
          },
            {
              headerName: "Route",
              headerTooltip: "Route",
              field: "routeName",
              editable:isEditAllowed  && GridConfig.isRouteNameEditable,
              pinned: "left",
              lockPosition: true,
              lockPinned: true,
              minWidth: constants.columnWidth.ROUTES,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              cellEditor: DropdownEditor,
              cellEditorPopup: true,
              cellEditorParams: {
                canAcceptNewValue: true,
              ...ROUTENAME_EDITOR_PARAMS,
              },
              suppressSizeToFit:true,
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter)
            },
            {
              headerName: "Pro#",
              field: "proId",
              headerTooltip: "Pro# ID",
              pinned: "left",
              lockPosition: true,
              lockPinned: true,
              minWidth: constants.columnWidth.W_150,
              cellClass: GridConfig.getProNumberClasses,
              cellRenderer: CopyTextCellRenderer,
              filter: MultiSelectFilter,
              filterParams:{
                filterType: constants.FILTER_TYPES.STRING
              },
            },
            {
              headerName: "Customer",
              headerTooltip: "Customer Name",
              field: "displayCustomerName",
              minWidth: constants.columnWidth.CUSTOMER,
              pinned: "left",
              lockPosition: true,
              lockPinned: true,
              cellClass: GridConfig.getCustomerNameClasses,
              filter: MultiSelectFilter,
              valueGetter : getCustomerName,
              tooltipValueGetter: (params) => params.value,
              filterParams:{
                width: constants.columnWidth.W_300
              }
            },
            {
              headerName: "Spl Needs",
              headerTooltip: "Special Needs",
              minWidth: constants.columnWidth.SPL_NEED,
              cellClass: "gridCell-leftAlign",
              valueGetter: (params) => GridConfig.specialNeedsValueGetter(params, featureFlags),
              filter: StaticFilter,
              filterParams: {
                getStaticOptions: GridConfig.getSpecialNeedsOption,
              },
              cellRendererParams:{
                getAPTWindow: aptConfig.valueGetter,
              },
              cellRenderer: SpecialNeedsRenderer,
              pinned: "left",
              lockPosition: true,
              lockPinned: true,
            },
            {
              headerName: "Address",
              headerTooltip: "Customer Address",
              field: "consigneeAddress",
              minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipValueGetter: (params) => params.value,
              valueGetter : getCustomerAddress,
              filterParams:{
                width: constants.columnWidth.W_300
              }
            },
            {
              headerName: "City",
              headerTooltip: "City",
              field: "city",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.CITY,
              filter: MultiSelectFilter,
              tooltipValueGetter: (params) => params.value,
              valueGetter : getCustomerCity,
            },
            {
              headerName: "State",
              headerTooltip: "State",
              field: "state",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.STATE,
              filter: MultiSelectFilter,
              valueGetter : GridConfig.getCustomerState,
            },
            {
              headerName: "Zip",
              headerTooltip: "Zip",
              field: "zip",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.ZIP_CODE,
              filter: MultiSelectFilter,
              valueGetter : GridConfig.getCustomerZip,
            },
            {
              headerName: "HU",
              headerTooltip: "Handling Unit",
              field: "hu",
              minWidth: constants.columnWidth.HU,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              editable: false,
              //cellEditor: NumericEditor,
            },
            {
              headerName: "Geocoding",
              headerTooltip: "Geocoding",
              valueGetter: GridConfig.geoCodeValueGetter,
              minWidth: constants.columnWidth.GEOCODING,
              cellClass: "gridCell-left,Align",
              editable: false,
              cellRenderer: GeoCodeRenderer,
              filter: StaticFilter,
              filterParams: {
                staticOptions: Object.values(constants.geoCode),
                renderIcon: iconRenderer
              },
            },
            {
              headerName: "Package Type",
              headerTooltip: "Package Type",
              field: "packageType",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.PACKAGE_TYPE,
              filter: MultiSelectFilter
            },
            {
              headerName: "Pallet Count",
              headerTooltip : "Pallet Count",
              field: "palletCount",
              minWidth: constants.columnWidth.PALLET_COUNT,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
            },
            {
              headerName: "Pieces",
              headerTooltip : "Pieces",
              field: "pieces",
              minWidth: constants.columnWidth.PIECES,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
            },
            {
              headerName: "PSE",
              field: "pse",
              minWidth: constants.columnWidth.PSE,
              editable: isEditAllowed,
              headerTooltip : "Pallet Space Equivalent",
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              cellEditor: NumericEditor,
              hide: featureFlags.IsWeightBasedRoutingEnabled
              //tooltipField: "pse"
            },
            {
              headerName: "Calculated PSE",
              field: "calculatedPse",
              minWidth: constants.columnWidth.CALCULATED_PSE,
              headerTooltip : "Calculated Pallet Space Equivalent",
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide: true
            }, 
            {
              headerName: "Weight",
              headerTooltip: "Weight",
              field: "weight",
              minWidth: constants.columnWidth.WEIGHT,
              cellClass: "gridCell-centerAlign",
              filter: MultiSelectFilter,
              editable: isEditAllowed,
              cellEditor: NumericEditor,
            },
            {
              headerName: "Shipment Status",
              headerTooltip: "Shipment Status",
              field: "shipmentStatus",
              minWidth: constants.columnWidth.SHIPMENT_STATUS,
              cellClass: 'gridCell-leftAlign',
              filter: MultiSelectFilter,
              tooltipField: 'shipmentStatus'
            },
            {
              headerName: "Due Date",
              headerTooltip: "Due Date",
              field: "dueDate",
              minWidth: constants.columnWidth.DUE_DATE,
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              }
            },
            {
              headerName: "Service",
              headerTooltip: "Service",
              field: "priority",
              minWidth: constants.columnWidth.PRIORITY,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter
            },
            {
              headerName: aptConfig.headerName,
              headerTooltip: aptConfig.headerTooltip,
              field: "shipmentCTA",
              minWidth: constants.columnWidth.EARLY_LATE_CTA,
              cellClass: "gridCell-leftAlign",
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.TIME,
                isTimeInterval: true,
                useGetterValue: true,
              },
              editable: isEditAllowed,
              valueGetter: aptConfig.valueGetter,
              valueFormatter: aptConfig.valueFormatter,
              cellEditorPopup: true,
              cellEditor: TimeRangeEditor,
              suppressKeyboardEvent: suppressKeyboardEventHandler((params) => params.editing),
              tooltipValueGetter: (params) => params.value,
            },
            {
              headerName: "Delivery Window",
              headerTooltip: "Delivery Window",
              field: "deliveryWindow",
              minWidth: constants.columnWidth.DELIVERY_WINDOW,
              cellClass: "gridCell-leftAlign",
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.TIME,
                isTimeInterval: true,
                useGetterValue: true,
              },
              valueFormatter: formatDeliveryWindow,
            },
            {
              headerName: "LG",
              headerTooltip: "Lift Gate",
              field: "liftgate",
              editable: isEditAllowed,
              minWidth: constants.columnWidth.LG,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              cellEditor: DropdownEditor,
              cellEditorPopup: true,
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter)
            },
            // {
            //   headerName: "Spl Needs",
            //   headerTooltip: "Special Needs",
            //   field: "specialNeeds",
            //   minWidth: constants.columnWidth.SPL_NEED,
            //   cellClass: "gridCell-leftAlign",
            //   filter: MultiSelectFilter
            // },
            // {
            //   headerName: "Equip Pref",
            //   headerTooltip: "Equipment Preference",
            //   field: "eqpmtPref",
            //   minWidth: constants.columnWidth.EQUIPMENT_PREFERENCE,
            //   cellClass: "gridCell-leftAlign",
            //   filter: MultiSelectFilter
            // },
            {
              headerName: "Area",
              headerTooltip: "Area",
              field: "zoneName",
              minWidth: constants.columnWidth.AREA,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter
            },
            {
              headerName: "Curr Trlr",
              headerTooltip: "Trailer ID",
              field: "trailerNumber",
              minWidth: constants.columnWidth.TRAILER_ID,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter
            },
            {
              headerName: "SCAC Code",
              headerTooltip: "SCAC Code",
              field: "trailerScac",
              hide: true,
              filter: MultiSelectFilter,
              width: constants.columnWidth.W_120,
              minWidth: constants.columnWidth.W_120,
              cellClass: "gridCell-leftAlign",
            },
            {
              headerName: "IB ETA",
              headerTooltip: "Shipment ETA",
              field: "shipmentEtaDateTime",
              minWidth: constants.columnWidth.IBETA,
              valueFormatter: ibEtaValueFormatter,
              cellRenderer: IBETARenderer,
              editable:isEditAllowed && GridConfig.IBETAEditable,
              cellEditor: DateTimeEditor,
              cellEditorPopup: true,
              cellEditorParams:{
                DATETYPE: constants.DATETYPES.DATETIME
              },
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME
              }
            },
            {
              headerName: "Apt Due Date",
              headerTooltip: "Apt Due Date",
              field: "aptDueDate",
              minWidth: constants.columnWidth.APT_DUE_DATE,
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              }
            },
            {
            headerName: `Orig ${terminalAccessLabel.suffix}`,
            headerTooltip: `Origin ${terminalAccessLabel.fullName}`,
              field: "originTerminal",
              minWidth: constants.columnWidth.TERMINAL,
            filter: MultiSelectFilter,
            },
            {
            headerName: `Curr ${terminalAccessLabel.suffix}`,
            headerTooltip: `Current ${terminalAccessLabel.fullName}`,
              field: "currentTerminal",
              minWidth: constants.columnWidth.TERMINAL,
            filter: MultiSelectFilter,
            },
            {
            headerName: `Next ${terminalAccessLabel.suffix}`,
            headerTooltip: `Next ${terminalAccessLabel.fullName}`,
              field: "nextTerminal",
              minWidth: constants.columnWidth.TERMINAL,
              filter:MultiSelectFilter
            },
            {
              headerName: `Dest ${terminalAccessLabel.suffix}`,
              headerTooltip: `Destination ${terminalAccessLabel.fullName}`,
              field: "destTerminal",
              minWidth: constants.columnWidth.TERMINAL,
              filter: MultiSelectFilter
            },
            // {
            //   headerName: "Wave",
            //   headerTooltip: "Wave",
            //   field: "wave",
            //   minWidth: constants.columnWidth.WAVE,
            //   cellClass: "gridCell-leftAlign",
            //   filter: MultiSelectFilter
            // },
            {
              headerName: "Curr Mfst",
              headerTooltip: "Manifest ID",
              field: "manifestNumber",
              minWidth: constants.columnWidth.MANIFEST_ID,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter
            },
            {
              headerName: "Return Reason",
              headerTooltip: "Return Reason",
              field: "returnCode",
              minWidth: constants.columnWidth.RETURN_REASON,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              //tooltipField: "returnCode"

            },
            {
              headerName: "ILINE",
              headerTooltip: "ILINE",
              field: "iLine",
              minWidth: constants.columnWidth.ILINE,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter
            },
            {
              headerName: "Order#",
              headerTooltip: "Order Number",
              field: "orderNumber",
              minWidth: constants.columnWidth.ORDER_NUMBER,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter
            },
            {
              headerName: "Cubic Feet",
              headerTooltip: "Cubic Feet",
              field: "cube",
              minWidth: constants.columnWidth.CUBIC_FEET,
              cellClass: "gridCell-centerAlign",
              filter: MultiSelectFilter,
            },
            {
              headerName: "Prep time",
              headerTooltip: "Preparation Time",
              field: "preparationTime",
              minWidth: constants.columnWidth.PREP_TIME,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              editable: isEditAllowed,
              cellEditor: NumericEditor,
            },
            {
              headerName: "Customer#",
              headerTooltip: "Customer Account Number",
              valueGetter: GridConfig.getCustomerCode,
              minWidth: constants.columnWidth.CUSTOMER_CODE,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipValueGetter: (params) => params.value,
              hide: true
            },
            {
              headerName: "Latitude",
              field: "latitude",
              minWidth: constants.columnWidth.LATITUDE,
              //editable: GridConfig.isEditable,
              headerTooltip : "Latitude",
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField: "latitude",
              hide: true
            },
            {
              headerName: "Longitude",
              field: "longitude",
              minWidth: constants.columnWidth.LONGITUDE,
              //editable: GridConfig.isEditable,
              headerTooltip : "Longitude",
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField: "longitude",
              hide: true
            },
            {
              headerName: "Act Arr Time",
              headerTooltip: "Actual Arrival Time",
              field: "actualArrivalTime",
              minWidth: constants.columnWidth.IBETA,
              cellClass: "gridCell-leftAlign",
              valueFormatter: formatDateTime,
              hide: true,
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME,
              }
            },
            {
              headerName: "Stop Seq",
              field: "stopSequence",
              headerTooltip: "Stop Sequence",
              minWidth: constants.columnWidth.STOP_SEQUENCE,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide:true
            },
            {
              headerName: "Ldg Zone",
              field: "loadingZone",
              headerTooltip: "Loading Zone",
              minWidth: constants.columnWidth.LDG_ZONE,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide: true,
            },
            {
              headerName: "Del Type",
              field: "rwwhen",
              headerTooltip: "Del Type",
              minWidth: constants.columnWidth.SHIPMENT_STATUS,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
            },
            {
              headerName: "REQ From",
              field: "mabdFrom",
              minWidth: constants.columnWidth.APT_DUE_DATE,
              //editable: GridConfig.isEditable,
              headerTooltip : "REQ From",
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              },
              hide: columnConfig !== COLUMN_CONFIG.SAIA,
            },
            {
              headerName: "REQ/MABD To",
              field: "mabdTo",
              minWidth: constants.columnWidth.APT_DUE_DATE,
              //editable: GridConfig.isEditable,
              headerTooltip : "REQ/MABD To",
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filter: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              },
              hide: columnConfig !== COLUMN_CONFIG.SAIA,
            },
            {
              headerName: "Shipper",
              field: "shipperName",
              headerTooltip: "Shipper Name",
              minWidth: constants.columnWidth.CUSTOMER,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField:"shipperName",
              filterParams:{
                width: constants.columnWidth.W_300
              }
            },
            {
              headerName: "Shipper #",
              field: "shipperCode",
              headerTooltip: "Shipper Code",
              minWidth: constants.columnWidth.CUSTOMER_CODE,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField:"shipperCode"
            },
            {
              headerName: "Shipper Address",
              valueGetter: GridConfig.getShipperFullAddress,
              headerTooltip: "Shipper Address",
              minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipValueGetter: (params) => params.value,
              filterParams:{
                width: constants.columnWidth.W_300
              }
            },
            {
              headerName: "Shipper City",
              field: "shipperCity",
              headerTooltip: "Shipper City",
              minWidth: constants.columnWidth.CITY,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField:"shipperCity"
            },
            {
              headerName: "Shipper State",
              field: "shipperState",
              headerTooltip: "Shipper State",
              minWidth: constants.columnWidth.SHIPPER_STATE,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField:"shipperState"
            },
            {
              headerName: "Shipper Zip",
              field: "shipperZip",
              headerTooltip: "Shipper Zip",
              minWidth: constants.columnWidth.SHIPPER_STATE,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField:"shipperZip"
            },
            {
              headerName: "Spl Instructions",
              field: "shipmentSpecialInstruction",
              headerTooltip: "Special Instructions",
              minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              tooltipField:"shipmentSpecialInstruction",
              hide: false
            },
            {
              headerName: "Min Trailer",
              headerTooltip: "Min Feasible Trailer Length",
              field: "minEquipmentSize",
              editable:isEditAllowed,
              minWidth: constants.columnWidth.MAX_TRAILER,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              cellEditor: DropdownEditor,
              cellEditorPopup: true,
              cellEditorParams: {
                editorFieldName:"equipmentSizes",
              },
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter)
            },
            {
              headerName: "Max Trailer",
              headerTooltip: "Max Feasible Trailer Length",
              field: "maxFeasibleTrailerLength",
              editable:isEditAllowed,
              minWidth: constants.columnWidth.MAX_TRAILER,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              cellEditor: DropdownEditor,
              cellEditorPopup: true,
              cellEditorParams: {
                editorFieldName:"equipmentSizes",
              },
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter)
            },
            {
              headerName: 'Fixed Trailer',
              headerTooltip: 'Fixed Trailer Length',
              field: 'fixedEquipmentSize',
              editable: isEditAllowed,
              minWidth: constants.columnWidth.FIXED_TRAILER,
              cellClass: 'gridCell-leftAlign',
              filter: MultiSelectFilter,
              cellEditor: DropdownEditor,
              cellEditorPopup: true,
              cellEditorParams: {
                editorFieldName: 'equipmentSizes'
              },
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter)
            },
            {
              headerName: "Last Updated",
              headerTooltip: "Last Updated",
              field: "lastUpdated",
              minWidth: constants.columnWidth.IBETA,
              cellClass: "gridCell-leftAlign",
              filter: DateTimeFilter,
              valueFormatter: formatDateTime,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME,
              }
            },              
            {
              headerName: "Shipment Codes",
              headerTooltip: "Shipment Codes",
              minWidth: constants.columnWidth.W_150,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              valueGetter : (params)=> (params.data.shipmentCodes ? [...params.data.shipmentCodes] : []).join(",")
            },
            {
              headerName: "OSD Code",
              headerTooltip: "OSD Code",
              field: "osdCode",
              minWidth: constants.columnWidth.W_130,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter
            },
            {
              headerName: "Bay Location",
              headerTooltip: "Bay Location",
              field: "bayLocation",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.W_130,
              filter: MultiSelectFilter,
              filterParams:{
                filterType: constants.FILTER_TYPES.STRING
              },
              comparator: customAlphanumericComparator,
            },
            {
              headerName: "STy",
              headerTooltip : "Shipment Type",
              field: "isDelivery",
              minWidth: constants.columnWidth.W_65,
              valueGetter: GridConfig.shipmentTypeValueGetter,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide: true
            },
            {
              headerName: "SVC Code",
              headerTooltip: "SVC Code",
              field: "serviceCode",
              minWidth: constants.columnWidth.W_100,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide : true,
            },
            {
              headerName: "SVC Description",
              headerTooltip: "SVC Description",
              field: "serviceDescription",
              minWidth: constants.columnWidth.W_140,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide : true,
            },
            {
              headerName: "CTy",
              headerTooltip: "Customer Type",
              field: "customerType",
              minWidth: constants.columnWidth.W_65,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide : true,
            },
            {
              headerName: "Bill to Code",
              headerTooltip: "Bill to Code",
              field: "billToCode",
              minWidth: constants.columnWidth.W_140,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide : true,
            },
            {
              headerName: "Cargo Van",
              headerTooltip: "Cargo Van Eligible",
              field: "isCargoVanEligible",
              minWidth: constants.columnWidth.W_120,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              editable: isEditAllowed,
              cellEditor: DropdownEditor,
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
              hide : true,
            },
            {
              headerName: "2 Man",
              headerTooltip: "Two Man Required",
              field: "isTwoManRequired",
              minWidth: constants.columnWidth.W_100,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              cellEditor: DropdownEditor,
              cellEditorPopup: true,
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
              editable: isEditAllowed,
              hide : true,
            },
            {
              headerName: "Order ID",
              headerTooltip: "Order ID",
              field: "referenceID",
              minWidth: constants.columnWidth.W_120,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide : true,
            },
            {
              headerName: "Movement ID",
              headerTooltip: "Movement ID",
              field: "referenceID2",
              minWidth: constants.columnWidth.W_120,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide : true,
            },
            {
              headerName: "Adv Pro#",
              headerTooltip: "Advance Pro Number",
              field: "advanceProNumber",
              minWidth: constants.columnWidth.W_120,
              cellClass: "gridCell-leftAlign",
              filter: MultiSelectFilter,
              hide : true,
            },
            {
              headerName: "Trailer Status",
              headerTooltip: "Trailer Status",
              field: "trailerStatus",
              minWidth: constants.columnWidth.W_120,
              cellClass: 'gridCell-leftAlign',
              filter: MultiSelectFilter,
              tooltipField: 'trailerStatus'
            },
            {
              headerName: "Revenue",
              headerTooltip: "Revenue",
              field: "revenue",
              minWidth: constants.columnWidth.W_120,
              cellClass: 'gridCell-leftAlign',
              filter: MultiSelectFilter,
              tooltipField: 'revenue'
            },
            {
              headerName: "Door#",
              headerTooltip: "Door Number",
              field: "doorNumber",
              minWidth: constants.columnWidth.W_100,
              cellClass: 'gridCell-leftAlign',
              filter: MultiSelectFilter,
            }           
          ]
      }
      defaultConfig.columns = defaultConfig.columns.filter(
          (column) => {
            switch (column.field) {
              case "deliveryWindow": return columnConfig !== COLUMN_CONFIG.SAIA;
              case "isTwoManRequired": return shouldShowTwoManInTrailerInventory;
              case "isCargoVanEligible": return enableNoCargoVanCustomer;
              case "fixedEquipmentSize": return enableFixedEquipmentSize;
              case "violation":
              case "routeName" : return !isShipementForecastView;
              case "destTerminal": return isShipementForecastView;
              case "revenue": return enableRevenueFieldsOnUI;
              default: return true
            }
          }
      );
      return defaultConfig;
    }
}